import { Link } from 'react-router-dom';
import { SERVICE_GROUPS, SERVICES, GEOGRAPHIES, HEADLINE_STATS } from '@shared';
import { SectionHead } from '../components/SectionHead';
import { ServiceCard } from '../components/ServiceCard';
import { useSeo } from '../lib/seo';

function Hero() {
  return (
    <section className="hero wordmark">
      <div className="container">
        <span className="eyebrow" style={{ justifyContent: 'center' }}>Omega Trade &amp; Finance</span>
        <h1 style={{ marginTop: 22, fontFamily: 'var(--font-display)' }}>
          Informed Capital.<br />
          <em style={{ color: 'var(--accent)', fontStyle: 'normal' }}>Precise Intelligence.</em>
        </h1>
        <p className="lead">
          A UK B2B consultancy delivering market intelligence, trade-instrument document
          support and curated introductions to authorised capital partners across the UK,
          GCC, India and Southeast Asia.
        </p>
        <div className="cta-row">
          <Link className="btn accent" to="/contact">Submit a brief</Link>
          <Link className="btn ghost" to="/services">Explore services</Link>
        </div>
      </div>
    </section>
  );
}

function StatRibbon() {
  return (
    <div className="container">
      <div className="stats">
        {HEADLINE_STATS.map((s) => (
          <div className="cell" key={s.k}>
            <div className="k">{s.k}</div>
            <div className="v">{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ServiceTeasers() {
  const featured = SERVICE_GROUPS.map((g) => SERVICES.find((s) => s.group === g.id)).filter(Boolean) as typeof SERVICES;
  return (
    <section className="section" style={{ background: 'var(--bg-soft)' }}>
      <div className="container">
        <SectionHead
          num="01"
          label="Our practice"
          title="Five practice areas. Twenty-two service lines."
          lead="Every Omega engagement is structured to sit cleanly outside the FCA regulatory perimeter — consultancy, research, document preparation, or pure introducer activity to authorised partners."
        />
        <div className="svc-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {featured.map((svc) => <ServiceCard key={svc.id} svc={svc} />)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
          <Link className="btn ghost" to="/services">See all 22 services →</Link>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { num: 'Step 01', h: 'Submit a brief',     b: 'Body corporates submit a request through the portal — entity confirmation and sophisticated-investor self-certification gate the form.' },
    { num: 'Step 02', h: 'Scope & engage',     b: 'We respond within two business days with scope, fee structure and a written engagement letter. No work begins without it.' },
    { num: 'Step 03', h: 'Deliver or introduce', b: 'Research, document packs and introductions are delivered through your secure portal. Money flows directly between you and the authorised counterparty.' },
    { num: 'Step 04', h: 'Track & refresh',    b: 'Status, deliverables and renewals are visible on your dashboard. KYC refresh and counterparty updates are scheduled automatically.' },
  ];
  return (
    <section className="section">
      <div className="container">
        <SectionHead
          num="02"
          label="How we work"
          title="A transparent, four-step engagement model."
          lead="Designed to keep our role unambiguously consultancy-and-introduction. Authorised activity stays with the authorised institution; commercial decisions stay with you."
        />
        <div className="steps">
          {steps.map((s) => (
            <div key={s.num} className="step-card">
              <span className="num">{s.num}</span>
              <div className="h">{s.h}</div>
              <div className="b">{s.b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GeographyBand() {
  return (
    <section className="section" style={{ background: 'var(--bg-soft)' }}>
      <div className="container">
        <SectionHead
          num="03"
          label="Footprint"
          title="Three offices. Six geographies. One operating standard."
          lead="Direct presence in London, Dubai and Indore; partner reach across Europe, North America and Southeast Asia."
        />
        <div className="geo-grid">
          {GEOGRAPHIES.map((g) => (
            <div key={g.code} className="geo-cell">
              <div className="code">{g.code}</div>
              <div className="lbl">{g.label}</div>
              <div className="city">{g.city}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingBand() {
  return (
    <section className="section">
      <div className="container">
        <SectionHead
          num="04"
          label="Pricing & payments"
          title="From 2%. Card or bank transfer. Multi-currency."
          lead="Every Omega engagement starts at a 2% baseline fee and scales with scope and demand. Engagement letters are written and signed before any work begins — no surprises, no off-list rates."
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginTop: 8 }}>
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', letterSpacing: 0.06, textTransform: 'uppercase' }}>Fee floor</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 38, letterSpacing: '-0.02em', marginTop: 8 }}>From 2%</div>
            <p style={{ marginTop: 10, fontSize: 13, color: 'var(--fg-soft)', lineHeight: 1.55 }}>
              The 2% baseline applies to every service line. Complex scope, expedited timelines and
              cross-border counterparty diligence increase the fee — quoted transparently in the
              engagement letter.
            </p>
          </div>
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', letterSpacing: 0.06, textTransform: 'uppercase' }}>Payment methods</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: '-0.01em', marginTop: 8 }}>Card · Bank transfer</div>
            <ul style={{ marginTop: 10, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['American Express', 'Visa', 'Mastercard', 'UnionPay'].map((n) => (
                <li key={n} className="chip">{n}</li>
              ))}
            </ul>
            <p style={{ marginTop: 12, fontSize: 13, color: 'var(--fg-soft)', lineHeight: 1.55 }}>
              Card payments via our Revolut Business merchant rail. Bank transfers (BACS / SWIFT)
              accepted with a unique payment reference issued per invoice.
            </p>
          </div>
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', letterSpacing: 0.06, textTransform: 'uppercase' }}>Currencies</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: '-0.01em', marginTop: 8 }}>Multi-currency</div>
            <ul style={{ marginTop: 10, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['GBP', 'USD', 'EUR', 'AED', 'INR', 'SGD'].map((c) => (
                <li key={c} className="chip">{c}</li>
              ))}
            </ul>
            <p style={{ marginTop: 12, fontSize: 13, color: 'var(--fg-soft)', lineHeight: 1.55 }}>
              Invoices in your preferred currency. Other ISO 4217 currencies on request — quoted at
              the engagement-letter stage to lock the rate.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RegBand() {
  return (
    <section className="reg-band">
      <div className="container inner">
        <div>
          <span className="eyebrow" style={{ color: 'color-mix(in oklab, var(--bg) 60%, transparent)' }}>Regulatory position</span>
          <h2 style={{ marginTop: 16 }}>Unregulated by design — and explicit about it.</h2>
          <p>
            Section 19 FSMA 2000 prohibits unauthorised regulated activity. Omega Trade is structured
            so every service falls outside that perimeter: B2B-only, no client money, no investment
            advice, no arranging that doesn’t sit under the RAO Article 15 / 29 introducer exemptions.
          </p>
        </div>
        <Link to="/regulatory">Read the full regulatory position →</Link>
      </div>
    </section>
  );
}

export function Home() {
  // Home must set SEO explicitly: without this, SPA navigation from another
  // page back to Home leaves that page's title/description/canonical in place.
  useSeo({
    title: 'Omega Trade and Finance — B2B Advisory, Capital Introductions & Trade Instruments',
    description: 'UK B2B consultancy delivering market intelligence, trade-instrument document support and curated introductions to authorised capital partners across the UK, GCC, India and Southeast Asia.',
    canonicalPath: '/',
  });
  return (
    <main>
      <Hero />
      <StatRibbon />
      <ServiceTeasers />
      <HowItWorks />
      <PricingBand />
      <GeographyBand />
      <RegBand />
    </main>
  );
}
